/**
 * IV flow-rate arithmetic for nursing-maths practice (coursework only, never for a real
 * infusion).
 *
 *   flow rate (mL/h)      = volume (mL) ÷ time (h)
 *   drip rate (gtt/min)   = volume (mL) × drop factor (gtt/mL) ÷ time (min)
 *                         = flow rate (mL/h) × drop factor (gtt/mL) × (1 h / 60 min)
 *   infusion time (min)   = volume (mL) ÷ flow rate (mL/h) × 60 min/h
 *
 * These are the dimensional-analysis forms taught in open nursing-maths texts, for example
 * Open RN, "Nursing Skills" 5.14 "IV Infusion by Gravity" (250 mL over 2 h at 10 gtt/mL
 * is 20.83 gtt/min, rounded to 21), and the same chapter's rule that with microdrip tubing
 * (60 gtt/mL) the number of drops per minute equals the number of mL per hour.
 */

export const MINUTES_PER_HOUR = 60;

/** The drop factors printed on common gravity tubing: macrodrip 10, 12, 15, 20; micro 60. */
export const DROP_FACTORS: readonly number[] = [10, 12, 15, 20, 60];

export function totalMinutes(hours: number, minutes: number): number {
  return hours * MINUTES_PER_HOUR + minutes;
}

/** mL per hour from a volume over a time in minutes. */
export function flowRate(volumeMl: number, minutes: number): number {
  return (volumeMl * MINUTES_PER_HOUR) / minutes;
}

/** Drops per minute from a volume, a drop factor and a time in minutes. */
export function dripRate(
  volumeMl: number,
  dropFactor: number,
  minutes: number
): number {
  return (volumeMl * dropFactor) / minutes;
}

/** Drops per minute from a pump-style rate in mL per hour. */
export function dripRateFromFlow(
  mlPerHour: number,
  dropFactor: number
): number {
  return (mlPerHour * dropFactor) / MINUTES_PER_HOUR;
}

/** Minutes for a volume to run at a rate in mL per hour. */
export function infusionMinutes(volumeMl: number, mlPerHour: number): number {
  return (volumeMl / mlPerHour) * MINUTES_PER_HOUR;
}

/** Minutes for a volume to run at a number of drops per minute. */
export function infusionMinutesFromDrip(
  volumeMl: number,
  dropFactor: number,
  dropsPerMinute: number
): number {
  return (volumeMl * dropFactor) / dropsPerMinute;
}

/** Round half up to a number of decimals, the usual classroom convention. */
export function roundHalfUp(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return (
    Math.round((value + Number.EPSILON * Math.sign(value)) * factor) / factor
  );
}

export interface Duration {
  hours: number;
  minutes: number;
}

/** A length of time in whole hours and the remaining minutes, rounded to the nearest minute. */
export function toDuration(totalMinutesValue: number): Duration {
  const rounded = Math.round(totalMinutesValue);
  return {
    hours: Math.floor(rounded / MINUTES_PER_HOUR),
    minutes: rounded % MINUTES_PER_HOUR,
  };
}
